/**
 * Pure formatting helpers for the gameweek lock progress segment. Kept out
 * of the component so the "what does the header say" logic is unit-tested
 * without rendering MUI.
 */

const nf = new Intl.NumberFormat('en-GB');

/** "1m 26s", "42s", "2h 05m". Null-safe. */
export function formatDuration(ms) {
  if (ms === null || ms === undefined || Number.isNaN(ms) || ms < 0) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  return `${seconds}s`;
}

/** Elapsed time for one phase: ended-started when finished, now-started while running. */
export function phaseElapsedMs(phase, nowMs) {
  if (!phase?.startedAt) return null;
  const start = Date.parse(phase.startedAt);
  if (Number.isNaN(start)) return null;
  if (phase.endedAt) {
    const end = Date.parse(phase.endedAt);
    return Number.isNaN(end) ? null : Math.max(0, end - start);
  }
  if (phase.state === 'running' || phase.state === 'failed') return Math.max(0, nowMs - start);
  return null;
}

/** "12,345 / 20,767 substitutions", plus " · 4/16 shards" while the worker's set exists. */
export function phaseProgressText(phase) {
  if (!phase) return '';
  const parts = [];
  if (phase.state === 'skipped') {
    parts.push('skipped');
  } else if (phase.total > 0) {
    parts.push(`${nf.format(phase.done)} / ${nf.format(phase.total)} ${phase.unit}`);
  } else if (phase.done > 0) {
    parts.push(`${nf.format(phase.done)} ${phase.unit}`);
  }
  if (phase.shards) parts.push(`${phase.shards.done}/${phase.shards.total} shards`);
  return parts.join(' · ');
}

/**
 * Header line: "Step 3 of 8 · Apply pending transfers · 1m 26s since deadline",
 * or "Lock complete · 2m 26s" / "Waiting for the deadline".
 */
export function lockHeadline(progress) {
  if (!progress || !progress.phases?.length) return 'No lock data';
  const total = progress.phases.length;
  if (!progress.deadlineCrossed) return 'Waiting for the deadline';
  const elapsed = formatDuration(progress.elapsedSinceDeadlineMs);
  if (progress.complete) {
    const lastAutomatic = [...progress.phases]
      .reverse()
      .find(p => p.endedAt && p.key !== 'gameplay_reenabled');
    const took =
      lastAutomatic && progress.phases[0]?.startedAt
        ? formatDuration(
            Date.parse(lastAutomatic.endedAt) - Date.parse(progress.phases[0].startedAt)
          )
        : null;
    return took ? `Lock complete · settled in ${took}` : 'Lock complete';
  }
  const index = progress.phases.findIndex(p => p.key === progress.currentPhase);
  const phase = index >= 0 ? progress.phases[index] : null;
  const step = phase ? `Step ${index + 1} of ${total} · ${phase.label}` : `Step ? of ${total}`;
  const failed = phase?.state === 'failed' ? ' · STALLED' : '';
  return elapsed ? `${step}${failed} · ${elapsed} since deadline` : `${step}${failed}`;
}

/** Poll faster while something is actually moving. */
export function pollIntervalMs(progress) {
  if (!progress?.deadlineCrossed || progress.complete) return 10000;
  return 3000;
}
